import { create } from 'zustand';
import { SoftwareType, getSoftwareResources } from '../types';

export type TransferKind = 'software' | 'file';

export interface Transfer {
  id: string;          // unique client-side ID
  kind: TransferKind;
  name: string;
  fileSizeMb: number;
  downloadedMb: number;
  effectiveSpeedMbit?: number; // if set, overrides the shared bandwidth calc
  // software kind
  softwareId?: string;
  softwareType?: SoftwareType;
  version?: number;
  // true when this "download" is an in-place version upgrade — its speed is the
  // synthetic upgrade rate (SoftwareUpgrade timing), NOT the player's line speed.
  isUpgrade?: boolean;
  // file kind
  fileName?: string;
  fileContent?: string;
  fileId?: string;
}

export interface TickResult {
  completedSoftware: string[];
  completedFiles:    Array<{ id: string; fileName: string; content: string; sizeMb: number }>;
}

interface TransferStore {
  transfers: Transfer[];
  addTransfer:    (t: Transfer) => void;
  removeTransfer: (id: string) => void;
  tickAll:        (deltaSec: number, internetMbit: number) => TickResult;
}

let _idCounter = 0;
export const newTransferId = () => `tr-${++_idCounter}-${Date.now()}`;

// IDs of transfers that the tick loop has already completed this session.
// restoreDownloads checks this to avoid re-processing a pending file while
// the completeDownload API call is still in-flight.
export const justCompletedIds = new Set<string>();

export const useTransferStore = create<TransferStore>((set, get) => ({
  transfers: [],

  addTransfer: (t) =>
    set(state => ({ transfers: [...state.transfers.filter(x => x.id !== t.id), t] })),

  removeTransfer: (id) =>
    set(state => ({ transfers: state.transfers.filter(t => t.id !== id) })),

  tickAll: (deltaSec, internetMbit) => {
    const { transfers } = get();
    if (transfers.length === 0) return { completedSoftware: [], completedFiles: [] };

    // Global shared bandwidth (MBps) divided among transfers that don't have their own speed
    const sharedMBps   = internetMbit / 8;
    const sharedCount  = transfers.filter(t => !t.effectiveSpeedMbit).length;

    const result: TickResult = { completedSoftware: [], completedFiles: [] };
    const updated: Transfer[] = [];

    for (const t of transfers) {
      const speedMBps = t.effectiveSpeedMbit
        ? t.effectiveSpeedMbit / 8
        : sharedCount > 0 ? sharedMBps / sharedCount : sharedMBps;

      const next = t.downloadedMb + speedMBps * deltaSec;
      if (next >= t.fileSizeMb) {
        justCompletedIds.add(t.id);
        if (t.kind === 'software' && t.softwareId) {
          result.completedSoftware.push(t.softwareId);
        } else if (t.kind === 'file') {
          result.completedFiles.push({
            id:       t.id,
            fileName: t.fileName ?? t.name,
            content:  t.fileContent ?? '',
            sizeMb:   t.fileSizeMb,
          });
        }
        // don't push — transfer is done
      } else {
        updated.push({ ...t, downloadedMb: next });
      }
    }

    set({ transfers: updated });
    return result;
  },
}));

export function softwareTransferSizeMb(type: SoftwareType, version: number): number {
  return getSoftwareResources(type, version).hddMb;
}

export function downloadTimeSec(
  fileSizeMb: number,
  internetMbit: number,
  effectiveSpeedMbit?: number,
  numActive = 1
): number {
  const speed = effectiveSpeedMbit ?? internetMbit / numActive;
  return fileSizeMb * 8 / Math.max(speed, 0.001);
}
